import { Typography } from "@/components/atoms";
import { DirhamIcon } from "@/components/atoms/icons/DirhamIcon";
import { OkDialog } from "@/components/molecules";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
// @ts-ignore Dynatrace service is ts-nocheck; runtime export is available
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { useAppStore } from "@/core/stores/appStore";
import { hasTextValue } from "@/core/utils/fieldAvailability";
import { isSmallScreen } from "@/core/utils/responsive";
import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { ProfileContactCardWeb } from "./ProfileContactCardWeb";
import { formatPriceInMillions } from "../../properties-web/utils/formatPrice";
import { resolveIskanLoanAmountAed } from "../../properties-web/utils/iskanLoan";

const MISSING_VALUE = "—";

export function LoanDetailsScreenWeb() {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const isMobileWeb = isSmallScreen();
  const loanDetails = useAppStore((s) => s.adhaLoanDetails);
  const profileLoanAmount = useAppStore((s) => s.auth.user?.loan_Amount);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const rawApplicationNumber = loanDetails?.adhaApplicationNumber;
  const applicationNumber = hasTextValue(rawApplicationNumber)
    ? rawApplicationNumber
    : MISSING_VALUE;

  const sanctionedAmount = resolveIskanLoanAmountAed(
    profileLoanAmount,
    loanDetails?.sanctionedLoanAmount,
  );

  const sanctionedDisplay = useMemo(
    () => formatPriceInMillions(sanctionedAmount, isRTL),
    [sanctionedAmount, isRTL],
  );

  useEffect(() => {
    trackDynatraceEvent({
      p_screen: "loan-details",
      p_actionName: "Loan Details Viewed",
      eventType: "view",
    });
  }, []);

  useEffect(() => {
    if (loanDetails == null) {
      setErrorMessage(t("profile.loanDetailsLoadError"));
      return;
    }
    setErrorMessage(null);
  }, [loanDetails, t]);

  return (
    <WebAppLayout
      activeTab={null}
      headerTitle={t("profile.loanDetails" as any)}
      showBackButton={true}
      breadcrumbs={[
        { label: t("common.home"), path: "/(auth)/(home)" },
        { label: t("profile.loanDetails" as any) },
      ]}
    >
      <View
        style={[
          styles.pageContainer,
          { backgroundColor: colors.backgroundLevel1 },
        ]}
      >
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.contentWrapper,
            { paddingHorizontal: isMobileWeb ? 16 : 32 },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.cardGroup}>
            <ProfileContactCardWeb
              title={t("profile.applicationNumber" as any)}
              value={
                <Typography
                  variant="body"
                  color="secondary"
                  style={{ textAlign: isRTL ? "right" : "left" }}
                >
                  {applicationNumber}
                </Typography>
              }
            />

            <ProfileContactCardWeb
              title={t("profile.sanctionedLoanAmount" as any)}
              value={
                <View
                  style={[
                    styles.amountRow,
                    { flexDirection: "row" },
                  ]}
                >
                  <DirhamIcon size={16} color={colors.fontSecondary} />
                  <Typography
                    variant="body"
                    color="secondary"
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    {sanctionedDisplay}
                  </Typography>
                </View>
              }
            />
          </View>
        </ScrollView>
      </View>

      <OkDialog
        visible={errorMessage !== null}
        message={errorMessage ?? ""}
        onOk={() => setErrorMessage(null)}
      />
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    borderRadius: 12,
  },
  scroll: {
    flex: 1,
  },
  contentWrapper: {
    paddingHorizontal: 32,
    paddingBottom: 32,
    paddingTop: 24,
    gap: 12,
  },
  cardGroup: {
    gap: 16,
    marginBottom: 16,
  },
  amountRow: {
    alignItems: "center",
    gap: 4,
  },
});

export default LoanDetailsScreenWeb;
